import React, { useMemo } from "react";
import { Button, Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

/**
 * Reusable export button for array data.
 * Props:
 * - data: Array of objects to export
 * - filename: Base filename without extension
 * - columns: Optional array of { key, label } to control column order and headers
 * - sheetName: Optional Excel sheet name
 */
export default function ExportButton({ data = [], filename = "export", columns, sheetName = "Veriler", excelOptions }) {
  const options = {
    title: sheetName,
    autoFilter: true,
    autoWidth: true,
    includeTitleInCSV: false,
    ...excelOptions,
  };

  // Convert column index to Excel letter (0 -> A)
  const colIdxToLetter = (idx) => {
    let s = "";
    let n = idx + 1;
    while (n > 0) {
      const m = (n - 1) % 26;
      s = String.fromCharCode(65 + m) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  };

  // Build AOA and compute widths based on data and provided columns
  const buildSheetContent = () => {
    const cols = columns && columns.length ? columns : (Array.isArray(data) && data.length > 0 ? Object.keys(data[0]).map(k => ({ key: k, label: k })) : []);
    const header = cols.map(c => c.label || c.key);
    const rows = (Array.isArray(data) ? data : []).map(row => cols.map(c => {
      const v = row?.[c.key];
      return v === null || v === undefined ? "" : v;
    }));

    const aoa = options.title ? [[options.title], header, ...rows] : [header, ...rows];

    // Compute column widths
    if (options.autoWidth) {
      const colCount = header.length;
      const startRowIdx = options.title ? 1 : 0; // header row index in aoa
      const widths = new Array(colCount).fill(10);
      for (let c = 0; c < colCount; c++) {
        // header length
        let maxLen = String(header[c] || "").length;
        // body lengths
        for (let r = startRowIdx + 1; r < aoa.length; r++) {
          const cell = aoa[r][c];
          const len = String(cell ?? "").length;
          if (len > maxLen) maxLen = len;
        }
        widths[c] = Math.max(10, Math.min(50, maxLen + 2));
      }
      return { aoa, widths };
    }
    return { aoa, widths: undefined };
  };

  const mappedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!columns || columns.length === 0) return data;
    return data.map((row) => {
      const mapped = {};
      columns.forEach((col) => {
        mapped[col.label || col.key] = row[col.key];
      });
      return mapped;
    });
  }, [data, columns]);

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetName);

    // Title
    if (options.title) {
      sheet.mergeCells("A1", `${String.fromCharCode(65 + columns.length - 1)}1`);
      const cell = sheet.getCell("A1");
      cell.value = options.title;
      cell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
      sheet.getRow(1).height = 28;
    }

    // Headers
    const headerRowIdx = options.title ? 2 : 1;
    const headers = columns.map(c => c.label || c.key);
    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } };
      cell.alignment = { horizontal: "center" };
      cell.border = {
        top: { style: "thin", color: { argb: "FF999999" } },
        left: { style: "thin", color: { argb: "FF999999" } },
        bottom: { style: "thin", color: { argb: "FF999999" } },
        right: { style: "thin", color: { argb: "FF999999" } },
      };
    });

    // Data rows
    data.forEach((row) => {
      const vals = columns.map(c => row[c.key]);
      const r = sheet.addRow(vals);
      r.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFDDDDDD" } },
          left: { style: "thin", color: { argb: "FFDDDDDD" } },
          bottom: { style: "thin", color: { argb: "FFDDDDDD" } },
          right: { style: "thin", color: { argb: "FFDDDDDD" } },
        };
      });
    });

    // Auto width
    sheet.columns.forEach((col) => {
      let max = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const val = cell.value ? cell.value.toString().length : 0;
        if (val > max) max = val;
      });
      col.width = max + 2;
    });

    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `${filename}.xlsx`);
  };

  const exportCSV = () => {
    // Optionally include title in CSV for consistency
    if (options.includeTitleInCSV && options.title) {
      const { aoa } = buildSheetContent();
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const csv = XLSX.utils.sheet_to_csv(ws, { FS: "," });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    const ws = XLSX.utils.json_to_sheet(mappedData);
    const csv = XLSX.utils.sheet_to_csv(ws, { FS: "," });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (

    <div>
    </div>
  );
}